import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';
import {
  Portlet,
  PortletActions,
  PortletBody,
  PortletCaption,
  PortletCaptionSubject,
  PortletTitle,
} from '../../components/Bootstrap/Portlet';
import {
  Col,
  ControlLabel,
  FormControl,
  Row,
} from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import { toNumber } from '../../utils/numbers';
import convertUnit from '../../utils/unit-converter';
import logger from '../../utils/logger';
import { displayDate } from '../../utils/date';

const SimpleTicketDetails = observer((props) => {
  const { ticket, t } = props;
  const { printStore, ticketStore, newAuthStore, materialStore, notifierStore, locationsStore } = useStores();
  const [ itemCode, setItemCode ] = useState(null);
  const [ saving, setSaving ] = useState(false);
  const [ ticketExist, setTicketExist ] = useState(true);
  const [ batchTicketId, setBatchTicketId ] = useState('');
  const { timezone } = ticketStore;
  const { materialsWithDetails } = materialStore;
  let data = ticketStore.simpleTickets?.find(v => v.id === ticket.id);

  const printTicket = () => {
    const html = document.querySelector('.ticket-details .tickets-item').innerHTML;
    printStore.printHtml(`
    <h2>Ticket #${ticket.id} ${ticket.isDemo ? t('Demo') : ''}</h2>
    ${html}
    `);
  };

  const checkBatchTicketExist = (ticketId) => {
    setBatchTicketId(ticketId);

    ticketStore.getTicketDetails(ticketId)
      .then(() => setTicketExist(true))
      .catch(() => setTicketExist(false));
  };

  const updateInventory = (data) => {
    const material = materialStore.materialsWithDetails?.find(v => v.itemId === data.itemId) || {};
    const quantity = toNumber(convertUnit(data.quantity, data.unitSymbol, material.itemInventoryUnitSymbol), 4);
    logger.log('inventory material', material.code, quantity, material.itemInventoryUnitSymbol);

    if (quantity > 0) {
      materialStore.updateMaterialInventoryUse(material, {
        quantity,
        date: data.date,
        comment: 'Manual',
        ticket: data.id,
        isAutomatic: false,
      });
    }
  };

  const updateTicket = () => {
    const material = materialsWithDetails?.find(v => v.itemCode === itemCode) || {};
    setSaving(true);

    ticketStore.updateSimpleTicket({
      ...ticket,
      itemCode: material.itemCode,
      itemId: material.itemId,
      batchTicketId,
    })
      .then(v => {
        if (v && !ticket.itemId && v.itemId) {
          updateInventory(v);
        }
        data = v;
      })
      .catch(e => notifierStore.showError({ key: 'Can\'t save ticket', props: { ticketId: ticket.id, error: e.message }}))
      .finally(() => setSaving(false));
  };

  const anchor = React.createRef();
  React.useEffect(() => {
    if (anchor.current) {
      anchor.current.scrollIntoView();
    }
  });

  return(
    <Portlet className="ticket-details"><span ref={anchor}/>
      <PortletTitle>
        <PortletCaption icon={'fa-file-text-o'}>
          <PortletCaptionSubject>{t('Ticket')} #{ticket.id} {ticket.isDemo ? <span className={'ticket-demo'}>{t('Demo')}</span> : ''}</PortletCaptionSubject>
        </PortletCaption>
        <PortletActions>
          <a className="btn btn-sm btn-simple hidden-xs" onClick={() => printTicket()}>
            <i className="fa fa-print"/> {t('Print')}
          </a>
        </PortletActions>
      </PortletTitle>
      <PortletBody>
        <div className="tickets-item">
          <div className="ticket-data">
            <Row>
              <Col sm={4}>
                <span className="ticket-field"><strong className="ticket-field-label">{t('Date')}: </strong>
                  <span>{displayDate(data.date, timezone, 'LT L', newAuthStore.user?.language)}</span>
                </span>
              </Col>
              <Col sm={4}>
                <span className="ticket-field"><strong className="ticket-field-label">{t('Plant')}: </strong>
                  <span>{locationsStore.currentPlant.description}</span>
                </span>
              </Col>
              <Col sm={4}>
                <span className="ticket-field"><strong className="ticket-field-label">{t('User')}: </strong>
                  <span>{data.userLogin}</span>
                </span>
              </Col>
              {!!data.itemCode && (
                <Col sm={4}>
                  <span className="ticket-field"><strong className="ticket-field-label">{t('Material')}: </strong>
                    {data.itemCode && <span>{data.itemCode}</span>}
                  </span>
                </Col>
              )}
              <Col sm={4}>
                <span className="ticket-field"><strong className="ticket-field-label">{t('Device')}: </strong>
                  <span>{data.deviceDescription}</span>
                </span>
              </Col>
              <Col sm={4}>
                <span className="ticket-field"><strong className="ticket-field-label">{t('Quantity')}: </strong>
                  <span>{data.quantity} {data.unitSymbol}</span>
                </span>
              </Col>
              {!!data.batchTicketId && (
                <Col sm={4}>
                  <span className="ticket-field"><strong className="ticket-field-label">{t('Regular Ticket ID')}: </strong>
                    <span>{data.batchTicketId}</span>
                  </span>
                </Col>
              )}
            </Row>
            <Row className="margin-top-15">
              {!data.itemCode && (
                <Col sm={4}>
                  <ControlLabel>{t('Material')}</ControlLabel>
                  <FormControl
                    name="itemCode"
                    componentClass="select"
                    value={itemCode || ''}
                    onChange={e => setItemCode(e.target.value)}
                    bsSize="sm"
                  >
                    <option/>
                    {materialsWithDetails.map(option => (
                      <option key={option.itemId} value={option.itemCode}>
                        {option.itemCode}
                      </option>
                    ))}
                  </FormControl>
                </Col>
              )}
              {!data.batchTicketId && (
                <Col sm={4}>
                  <ControlLabel>
                    {t('Regular Ticket ID')} {!!batchTicketId && ticketExist && <i className="fa fa-check text-success"/>}
                  </ControlLabel>
                  <FormControl
                    name="batchTicketId"
                    type="text"
                    value={batchTicketId || ''}
                    onChange={e => checkBatchTicketExist(e.target.value)}
                    bsSize="sm"
                  />
                </Col>
              )}
            </Row>

            {(!data.itemId || !data.batchTicketId) && (
              <Row className="text-right margin-top-15">
                <Col sm={12}>
                  <LoadingButton
                    bsStyle="primary"
                    onClick={updateTicket}
                    label={t('Save')}
                    loadingLabel={t('Saving')}
                    isLoading={saving}
                  />
                </Col>
              </Row>
            )}
          </div>
        </div>
      </PortletBody>
    </Portlet>
  );
});

export default withTranslation()(SimpleTicketDetails);
